import * as Inovx from '@shared';
import ViewerConstants from '../constants/ViewerConstants';
import AssetServices from '../services/AssetServices';
import FacilityStore from '../stores/FacilityStore';
import ViewerStore from '../stores/ViewerStore';
import AssetSetsActions from './AssetSetsActions';
import FacilityConstants from './../constants/FacilityConstants';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var FormHelper = Inovx.Shared.Common.FormHelper;

class AssetActions {
    constructor() {
    }

    async createAssetTag(tag: IAssetTag) {
        if (!SystemStore.isActionInProgress(FacilityConstants.ASSETTAG_ADD)) {
            SystemActions.beginAction(FacilityConstants.ASSETTAG_ADD);

            try {
                const facility = FacilityStore.getSelectedFacility();

                const tagText = tag.Text.toLocaleLowerCase();
                const existingAsset = facility.Assets.find(asset => asset.Identity.toLocaleLowerCase() == tagText && asset.AssetClassID == tag.AssetClass.ID);
                tag.AssetID = existingAsset?.ID;

                const data = FormHelper.trim(tag);
                const apiResponse = await AssetServices.createAssetTag({
                    FacilityID: facility.ID,
                    AssetClassID: data.AssetClass.ID,
                    AssetIdentity: data.Text,
                    ...data
                });

                const asset: IAssetIdentity = { ...apiResponse.Asset };
                const nativeAsset: INativeAsset = { ...apiResponse.NativeAsset };
                const assetTag: IAssetTag = { Text: null, ...apiResponse.Tag };
                this._dispatchAction(FacilityConstants.FACILITY_REFRESH_ASSETTAG, { tag: assetTag, asset, nativeAsset });
                this._ensureAssetTagClassVisible(data.AssetClass?.ID ?? assetTag.AssetClass?.ID ?? asset?.AssetClassID);

                // Asset-tag changes can update membership, query caches, and version tokens.
                await AssetSetsActions.refreshAssetSetsAfterExternalChange(facility.ID);

                return assetTag.ID;
            }
            finally {
                SystemActions.endAction(FacilityConstants.ASSETTAG_ADD);
            }
        }
        else {
            this._sendInvalidSave();
        }
    }

    async editAssetTag(tag: IAssetTag, actionConfirmed: boolean) {
        if (!SystemStore.isActionInProgress(FacilityConstants.ASSETTAG_EDIT)) {

            SystemActions.beginAction(FacilityConstants.ASSETTAG_EDIT);

            try {
                const facility = FacilityStore.getSelectedFacility();

                const data = FormHelper.trim(tag);

                const apiResponse = await AssetServices.editAssetTag({
                    FacilityID: facility.ID,
                    AssetClassID: data.AssetClass.ID,
                    AssetIdentity: data.Text,
                    ...data
                }, actionConfirmed);
                const asset: IAssetIdentity = { ...apiResponse.Asset };
                const assetTag: IAssetTag = { Text: null, ...apiResponse.Tag };

                this._dispatchAction(FacilityConstants.FACILITY_REFRESH_ASSETTAG, { tag: assetTag, asset: asset, deletedAssetID: apiResponse.DeletedAssetID });
                this._dispatchAction(ViewerConstants.VIEWER_UPDATE_ANNOTATION, null);
                this._ensureAssetTagClassVisible(data.AssetClass?.ID ?? assetTag.AssetClass?.ID ?? asset?.AssetClassID);

                // Asset-tag changes can update membership, query caches, and version tokens.
                await AssetSetsActions.refreshAssetSetsAfterExternalChange(facility.ID);
            }
            finally {
                SystemActions.endAction(FacilityConstants.ASSETTAG_EDIT);
            }
        } else {
            this._sendInvalidSave();
        }        
    }

    async deleteAssetTag(tag: IAssetTag, deleteParentAsset: boolean) {
        if (!SystemStore.isActionInProgress(FacilityConstants.ASSETTAG_DELETE)) {
            SystemActions.beginAction(FacilityConstants.ASSETTAG_DELETE);
            
            try {
                await AssetServices.deleteAssetTag(tag, deleteParentAsset);
                this._dispatchAction(FacilityConstants.FACILITY_DELETE_ASSETTAG, {
                    deletedTagID: tag.ID,
                    deletedAssetID: (deleteParentAsset) ? tag.AssetID : null
                });

                // Asset-tag changes can update membership, query caches, and version tokens.
                await AssetSetsActions.refreshAssetSetsAfterExternalChange(FacilityStore.getSelectedFacility()?.ID);
            }
            finally {
                SystemActions.endAction(FacilityConstants.ASSETTAG_DELETE);
            }
        }
    }

    async getAttributesInformation(assetId: string) {
        const data = await AssetServices.getAssetAttributes(assetId);
        this._dispatchAction(ViewerConstants.SET_ATTRIBUTES_INFORMATION, data);
        return data;
    }

    async resetAssetTag(id: string) {
        const apiResponse = await AssetServices.getAssetTag(id);
        const assetTag: IAssetTag = { Text: null, ...apiResponse };

        this._dispatchAction(FacilityConstants.FACILITY_REFRESH_ASSETTAG, { tag: assetTag, asset: null });
    }

    private _sendInvalidSave() {
        this._dispatchAction(FacilityConstants.ASSETTAG_INVALID_ADD);
    }

    private _ensureAssetTagClassVisible(assetClassID?: string) {
        if (!assetClassID) return;

        const visibleClassIDs = ViewerStore.getVisibleAssetTagClasses();
        if (!visibleClassIDs || visibleClassIDs.includes(assetClassID)) return;

        this._dispatchAction(ViewerConstants.VIEWER_SET_VISIBLE_ASSET_TAG_CLASSES, [...visibleClassIDs, assetClassID]);
    }

    getAssetTagsForViewer(assetTags: IAssetTag[]) {
        const grouped = assetTags.reduce((map: Map<string, IAssetTag[]>, tag: IAssetTag) => {
            let tags: IAssetTag[] = map.get(tag.AssetID);
            if (!tags) {
                tags = [tag];
                map.set(tag.AssetID, tags);
            }
            else {
                tags.push(tag);
            }

            return map;
        }, new Map<string, IAssetTag[]>());

        // Simply get a flat array of tags
        const result: IAssetTag[] = [];
        grouped.forEach((tags: IAssetTag[]) => {
            result.push(...tags);
        });
        return result;
    }

    matchesTagSelectedOntology(assetTag: IAssetTag, selectedOntologyID: string): boolean {
        if (!selectedOntologyID) {
            return true;
        }

        const applicableOntologyIDs = assetTag.ApplicableOntologyIDs;
        return !!applicableOntologyIDs && applicableOntologyIDs.indexOf(selectedOntologyID) >= 0;
    }

    filterTagsBySelectedOntology(assetTags: IAssetTag[], selectedOntologyID: string): IAssetTag[] {
        return assetTags.filter(assetTag => this.matchesTagSelectedOntology(assetTag, selectedOntologyID));
    }

    createAssetTagsTree(facility: IFacilityDetail) {
        const { Assets, AssetTags } = facility;
        // Create a map of assetTags based on AssetID
        const assetTagsMap = AssetTags.reduce((map, tag) => {
            const assetID = tag.AssetID;
            if (!map.has(assetID)) {
                map.set(assetID, []);
            }
            map.get(assetID).push(tag);
            return map;
        }, new Map());

        // Merge assets and assetTags based on ID and AssetID
        const mergedAssets = Assets.map(asset => {
            const tags = assetTagsMap.get(asset.ID) || [];
            return { ...asset, Tags: tags };
        });

        return mergedAssets;
    }

    async executeAssetQuery(ontologyId: string, apiRules: unknown, cleanedRules: unknown): Promise<string[]> {
        try {
            const result = await AssetServices.executeQuery(ontologyId, apiRules);
            let assetIds: string[] = [];
            
            // Extract asset IDs from the query result and apply filter
            if (result && Array.isArray(result)) {
                assetIds = result.map((item: any) => item.ID || item.id || item.AssetID || item.assetId).filter(Boolean);
                const currentFilter = ViewerStore.getAssetTagsFilter() || {};
                // Pass the array even if empty - empty array means no assets match the query
                // Store the cleaned (but untransformed) rules for client-side restoration
                this.updateAssetSearchFilter({
                    ...currentFilter,
                    AssetIdFilter: result.length === 0 ? [] : assetIds,
                    QueryRules: cleanedRules,
                    QueryApiRules: apiRules,
                    QueryOntologyID: ontologyId
                });
            }

            return assetIds;
        } catch (error) {
            console.error('Failed to execute query:', error);
            throw error;
        }
    }

    clearAssetQuery(): void {
        const currentFilter = ViewerStore.getAssetTagsFilter() || {};
        this.updateAssetSearchFilter({
            ...currentFilter,
            AssetIdFilter: undefined,
            QueryRules: undefined,
            QueryApiRules: undefined,
            QueryOntologyID: undefined
        });
    }

    updateAssetSearchFilter(filterData: IAssetFilter) {
        const facility = FacilityStore.getSelectedFacility();
        const assets = facility?.Assets || [];
        const nativeAssets = facility?.NativeAssets || [];
        const assetTags = facility?.AssetTags || [];
        const filteredAssetIDs: string[] = [];
        const selectedAnnotation = ViewerStore.getSelectedAnnotation();
        let resetSelectedAnnotation = false;

        if (filterData) {
            const hasAssetIdFilter = filterData.AssetIdFilter !== undefined && filterData.AssetIdFilter !== null;
            const hasNameFilter = !!filterData.NameFilter;
            const hasAssetClassFilter = !!filterData.AssetClassFilter;
            const placementFilter = filterData.PlacementStatusFilter;
            const hasPlacementFilter = !!placementFilter && placementFilter !== "All";

            if (!hasAssetIdFilter && !hasNameFilter && !hasAssetClassFilter && !hasPlacementFilter) {
                this._dispatchAction(ViewerConstants.ASSET_TAGS_FILTER, { filterData, filteredAssetIDs });
                return;
            }

            let selectedAnnotationAssetID: string = null;
            if (selectedAnnotation) {
                selectedAnnotationAssetID = assetTags.find((tag: IAssetTag) => tag.ID === selectedAnnotation)?.AssetID || null;
            }

            if (hasAssetIdFilter && filterData.AssetIdFilter.length === 0) {
                for (let i = 0; i < assets.length; i++) {
                    filteredAssetIDs.push(assets[i].ID);
                }

                if (selectedAnnotationAssetID) {
                    resetSelectedAnnotation = true;
                }

                this._dispatchAction(ViewerConstants.ASSET_TAGS_FILTER, { filterData, filteredAssetIDs });
                if (resetSelectedAnnotation) {
                    this._dispatchAction(ViewerConstants.VIEWER_RESET_ANNOTATION);
                }
                return;
            }

            const allowedAssetIdSet = hasAssetIdFilter ? new Set(filterData.AssetIdFilter) : null;
            const assetClassFilterSet = hasAssetClassFilter ? new Set(filterData.AssetClassFilter) : null;
            const nameFilterLower = hasNameFilter ? filterData.NameFilter.toLowerCase() : "";

            let representedIn3DAssetIDs: Set<string> = null;
            let representedIn2DAssetIDs: Set<string> = null;
            let taggedAssetIDs: Set<string> = null;

            if (placementFilter === "RepresentedIn3D") {
                representedIn3DAssetIDs = new Set<string>();
                for (let i = 0; i < nativeAssets.length; i++) {
                    const nativeAsset = nativeAssets[i];
                    if (nativeAsset.Source === AssetSourceType.Vnf && nativeAsset.AssetID) {
                        representedIn3DAssetIDs.add(nativeAsset.AssetID);
                    }
                }
            }
            else if (placementFilter === "RepresentedIn2D") {
                representedIn2DAssetIDs = new Set<string>();
                for (let i = 0; i < nativeAssets.length; i++) {
                    const nativeAsset = nativeAssets[i];
                    if (nativeAsset.Source === AssetSourceType.DocX && nativeAsset.AssetID) {
                        representedIn2DAssetIDs.add(nativeAsset.AssetID);
                    }
                }
            }
            else if (placementFilter === "ManuallyTagged") {
                taggedAssetIDs = new Set<string>();
                for (let i = 0; i < assetTags.length; i++) {
                    const assetTag = assetTags[i];
                    if (assetTag.AssetID) {
                        taggedAssetIDs.add(assetTag.AssetID);
                    }
                }
            }

            for (let i = 0; i < assets.length; i++) {
                const asset = assets[i];
                let shouldHide = false;

                if (hasAssetIdFilter && !allowedAssetIdSet.has(asset.ID)) {
                    shouldHide = true;
                }

                if (!shouldHide && hasNameFilter && !asset.Identity.toLowerCase().includes(nameFilterLower)) {
                    shouldHide = true;
                }

                if (!shouldHide && hasAssetClassFilter && !assetClassFilterSet.has(asset.AssetClassID)) {
                    shouldHide = true;
                }

                if (!shouldHide && hasPlacementFilter) {
                    switch (placementFilter) {
                        case "RepresentedIn3D":
                            if (!representedIn3DAssetIDs.has(asset.ID)) shouldHide = true;
                            break;
                        case "RepresentedIn2D":
                            if (!representedIn2DAssetIDs.has(asset.ID)) shouldHide = true;
                            break;
                        case "ManuallyTagged":
                            if (!taggedAssetIDs.has(asset.ID)) shouldHide = true;
                            break;
                    }
                }

                if (shouldHide) {
                    filteredAssetIDs.push(asset.ID);
                    if (!resetSelectedAnnotation && selectedAnnotationAssetID === asset.ID) {
                        resetSelectedAnnotation = true;
                    }
                }
            }
        }

        this._dispatchAction(ViewerConstants.ASSET_TAGS_FILTER, { filterData, filteredAssetIDs });
        if (resetSelectedAnnotation) {
            this._dispatchAction(ViewerConstants.VIEWER_RESET_ANNOTATION);
        }
    }

    async updateAssetAttribute(attributes: IAssetAttributeRequest) {
        await AssetServices.updateAssetAttribute(attributes);
        this.getAttributesInformation(attributes.AssetIdentityID);

        // Attribute changes can invalidate query sets and advance their version tokens.
        await AssetSetsActions.refreshAssetSetsAfterExternalChange(FacilityStore.getSelectedFacility()?.ID);
    }

    private _dispatchAction(actionType: string, data?: any) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: actionType,
            data: data
        });
    }
}

const assetActions = new AssetActions();

export default assetActions;
